/**
 * Shared lightmap apply logic for editor and runtime.
 */
import * as THREE from 'three';

import { MeshComponent } from '../components/visual/MeshComponent.js';
import { ModelMeshComponent } from '../components/visual/ModelMeshComponent.js';

import { buildDataZipAssetPathForScene, loadLightmapBuildData } from './lightmapBuildData.js';

import type { LightmapBuildData } from './lightmapBuildData.js';
import type { Actor } from '../actors/Actor.js';
import type { SceneComponent } from '../components/SceneComponent.js';
import type { World } from '../game/World.js';

const textureLoader = new THREE.TextureLoader();

/** Stable manifest / export id from the scene component UUID. */
export function lightmapperObjectId(component: SceneComponent): string {
  return component.uuid;
}

export interface LightmapPrototypeUv2Sidecar {
  /** Always 'perVertex' for the current bake output. */
  mode?: 'perVertex';
  /** Must equal `geometry.attributes.position.count` on the runtime mesh. */
  vertexCount: number;
  /** Flat (u, v) pairs, length = 2 * vertexCount. */
  uv: number[];
  seamVertexCount?: number;
  maxSeamSpread?: number;
}

export interface LightmapManifestEntry {
  id: string;
  lightMap: string;
  uv2: string;
  lightMapIntensity?: number;
  uvAttribute?: string;
  seamVertexCount?: number;
  maxSeamSpread?: number;
}

export interface LightmapManifest {
  lightmapResolution?: number;
  uvChannel?: number;
  objects: LightmapManifestEntry[];
}

export interface LightmapManifestApplyResult {
  applied: string[];
  skipped: Array<{ id: string; reason: string }>;
}

type LightmapCapableMaterial = THREE.Material & {
  lightMap: THREE.Texture | null;
  lightMapIntensity: number;
  needsUpdate: boolean;
};

function isLightmapCapableMaterial(material: THREE.Material): material is LightmapCapableMaterial {
  return 'lightMap' in material && 'lightMapIntensity' in material;
}

function meshSupportsLightmap(mesh: THREE.Mesh): boolean {
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  return materials.length > 0 && materials.every((material) => isLightmapCapableMaterial(material));
}

export async function applyLightmap(
  mesh: THREE.Mesh,
  buildData: LightmapBuildData,
  entry: LightmapManifestEntry,
): Promise<void> {
  const uv2 = buildData.getUv2Json<LightmapPrototypeUv2Sidecar>(entry.uv2);
  const vertexCount = mesh.geometry.attributes.position.count;
  if (vertexCount !== uv2.vertexCount) {
    throw new Error(
      `${mesh.name} uv2 mismatch: geometry has ${vertexCount} vertices, uv2 sidecar has ${uv2.vertexCount}`,
    );
  }
  if (uv2.uv.length !== 2 * uv2.vertexCount) {
    throw new Error(
      `${mesh.name} uv2 sidecar malformed: uv length ${uv2.uv.length} != 2 * vertexCount ${uv2.vertexCount}`,
    );
  }

  mesh.geometry.setAttribute('uv2', new THREE.Float32BufferAttribute(uv2.uv, 2));

  if (uv2.seamVertexCount && uv2.seamVertexCount > 0) {
    console.warn(
      `[lightmapApply] ${mesh.name}: ${uv2.seamVertexCount}/${uv2.vertexCount} vertices straddle ` +
        `lightmap seams (max spread ${uv2.maxSeamSpread?.toFixed(4) ?? '?'}). uv2 was averaged; ` +
        'consider splitting seams in the source mesh to remove the smear.',
    );
  }

  const lightmapUrl = buildData.getLightmapBlobUrl(entry.lightMap);
  const lightMap = await textureLoader.loadAsync(lightmapUrl);
  lightMap.flipY = true;
  lightMap.channel = 2;
  lightMap.colorSpace = THREE.NoColorSpace;
  lightMap.needsUpdate = true;

  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const material of materials) {
    if (!isLightmapCapableMaterial(material)) {
      continue;
    }
    material.lightMap = lightMap;
    material.lightMapIntensity = entry.lightMapIntensity ?? 1.0;
    material.needsUpdate = true;
  }
}

export async function buildLightmapperMeshIndex(
  world: World,
  excludeActor?: Actor,
): Promise<Map<string, THREE.Mesh[]>> {
  const index = new Map<string, THREE.Mesh[]>();

  const addMeshes = (id: string, meshes: THREE.Mesh[]) => {
    if (meshes.length === 0) {
      return;
    }
    const existing = index.get(id);
    if (existing) {
      existing.push(...meshes);
    } else {
      index.set(id, meshes);
    }
  };

  for (const actor of world.getActors()) {
    if (actor === excludeActor) {
      continue;
    }

    for (const component of actor.getComponents(MeshComponent)) {
      if (component.isEditorOnly()) {
        continue;
      }
      addMeshes(lightmapperObjectId(component), [component.mesh]);
    }

    for (const component of actor.getComponents(ModelMeshComponent)) {
      if (component.isEditorOnly()) {
        continue;
      }
      await component.waitForLoad();
      addMeshes(lightmapperObjectId(component), component.getAllMeshes());
    }
  }

  return index;
}

async function applyManifestEntry(
  entry: LightmapManifestEntry,
  meshIndex: Map<string, THREE.Mesh[]>,
  buildData: LightmapBuildData,
  result: LightmapManifestApplyResult,
): Promise<void> {
  const meshes = meshIndex.get(entry.id);
  if (!meshes || meshes.length === 0) {
    result.skipped.push({ id: entry.id, reason: 'no matching MeshComponent/ModelMeshComponent in scene' });
    return;
  }

  const applicableMeshes = meshes.filter((mesh) => meshSupportsLightmap(mesh));
  if (applicableMeshes.length === 0) {
    result.skipped.push({ id: entry.id, reason: 'mesh material does not support light maps' });
    return;
  }

  try {
    for (const mesh of applicableMeshes) {
      await applyLightmap(mesh, buildData, entry);
    }
    result.applied.push(entry.id);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    result.skipped.push({ id: entry.id, reason: message });
    console.error(`[lightmapApply] failed to apply lightmap for ${entry.id}:`, err);
  }
}

export async function applyManifestToWorld(
  world: World,
  sceneAssetPath: string,
  excludeActor?: Actor,
): Promise<LightmapManifestApplyResult> {
  const buildData = await loadLightmapBuildData(buildDataZipAssetPathForScene(sceneAssetPath));
  try {
    const meshIndex = await buildLightmapperMeshIndex(world, excludeActor);
    const result: LightmapManifestApplyResult = { applied: [], skipped: [] };

    for (const entry of buildData.manifest.objects) {
      await applyManifestEntry(entry, meshIndex, buildData, result);
    }

    return result;
  } finally {
    buildData.dispose();
  }
}
